import "vue-app/utils/pubSubInitializer";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";
new ReactNativeBridge({ withRegistry: true });

import { createApp } from "vue";
import "../assets/scss/main.scss";
import "./icons";
import "../assets/scss/main.css";
import { appStore } from "../../assets/js/app/store";
import router from "../../routes";
import { clearIntervals, globalLoaderHook, roleCheckHook } from "../../assets/js/app/global/router-hooks";
import axios from "vue-app/utils/axios";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { inspect } from "@xstate/inspect";
import "vue-toastification/dist/index.css";
import "../plugins/edroneToast/edrone-toast";
import "../thirdParty/hotjar";
import { whichXStateInspectorRun } from "vue-app/utils/xstate";
import TranslatePlugin from "vue-app/plugins/edroneTranslate/edroneTranslatePlugin";
import EdroneContextPlugin from "vue-app/plugins/edroneContext/edroneContextPlugin";
import VueNativeSock from "vue-native-websocket-vue3";
import vClickOutside from "click-outside-vue3";
import "../thirdParty/intercomInitScript";
import { useStore as useVuexStore } from "vuex";
import { useStore } from "vue-app/composables/useStore";
import { useRouter } from "vue-app/composables/useRouter";
import "../thirdParty/awsCloudWatchRUM";
import { createPinia } from "pinia";
import { startPingUser } from "vue-app/utils/ping";
import "../composables/useFirebase";
import { VueQueryPlugin } from "@tanstack/vue-query";
import DatePickerPlugin from "vue-app/plugins/datePicker/datePickerPlugin";
import { SUSPENSION_REASONS } from "vue-app-new/modules/Account/features/SuspensionMessage/SuspensionMessage.constants";

if (whichXStateInspectorRun() === "xstate4") {
  inspect({
    iframe: false,
  });
}

const pinia = createPinia();

const app = createApp({});
app.use(router);
app.use(appStore);
app.use(VueNativeSock, "wss://qev7les36f.execute-api.eu-west-1.amazonaws.com", {
  connectManually: true,
});
app.component("FontAwesomeIcon", FontAwesomeIcon);
app.use(TranslatePlugin);
app.use(EdroneContextPlugin);
app.use(vClickOutside);

const stopPingUser = startPingUser();

app.use(pinia);
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: 3000,
        staleTime: 1000 * 60,
      },
    },
  },
});
app.use(DatePickerPlugin);
app.mount("#app");

window.addEventListener("beforeunload", stopPingUser);

axios.interceptors.response.use(
  (response) => {
    if (response.headers["current-logged-user-data"]) {
      const currentUser = JSON.parse(atob(response.headers["current-logged-user-data"]));
      window.EDRONE_CONTEXT.user = {
        isDev: currentUser.isDev === 1,
        isPartner: currentUser.partner,
        canSetIsDev: currentUser.canSetIsDev,
        firstName: currentUser.firstname,
        lastName: currentUser.lastname,
        avatar: currentUser.avatar,
        lang: currentUser.lang,
        email: currentUser.email,
        agentId: currentUser.agentID,
        currency: currentUser.currency,
        notificationSound: currentUser.notificationSound,
        inboxNotification: currentUser.inboxNotification,
        inboxSendWithEnter: currentUser.inboxSendWithEnter,
        newMenu: currentUser.newMenu,
        companyUrl: currentUser.companyUrl,
        isSupport: currentUser.isSupport,
        isEmailVerified: currentUser.isEmailVerified,
        subdomain: currentUser.subdomain,
        roles: currentUser.roles,
        trackers: currentUser.trackers,
        isMfaEnabled: currentUser.isMfaEnabled,
      };
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

useRouter(router);

router.beforeEach((to, from, next) => {
  const store = useVuexStore(); // use the store from vuex here, to be sure that it is already installed
  useStore(store);

  if (to.path.startsWith("/app/products") && !window.EDRONE_CONTEXT?.hasAccessToFeed) {
    next("/");
  }

  if (to.path.startsWith("/app/onboarding") && window.EDRONE_CONTEXT.tracker.showOnboardings !== true) {
    next("/app/live-events");
  }

  next();
});
router.beforeEach(clearIntervals());
router.beforeEach(roleCheckHook(appStore));
router.beforeEach(globalLoaderHook());
router.beforeEach((to, from, next) => {
  if (window.onsiteAutosaveInterval) {
    clearInterval(window.onsiteAutosaveInterval);
  }
  next();
});
router.beforeEach((to, from, next) => {
  if (to.meta.isDev && !window.EDRONE_CONTEXT.user.isDev) {
    next("/");
  }

  next();
});
router.beforeEach((to, _, next) => {
  const lifecycle = window.EDRONE_CONTEXT.tracker.lifecycle;
  const isAccessBlockedPage = to.name === "PageAccessBlocked";
  const isCancellationPage = to.name === "PageCancellation";
  const isBillingPage = to.name === "PageAccountBilling";
  const isSupport = window.EDRONE_CONTEXT.user.isSupport;
  const isDev = window.EDRONE_CONTEXT.user.isDev;

  const canAccessBilling = [SUSPENSION_REASONS.BILLING_PLAN_EXCEEDED, SUSPENSION_REASONS.OVERDUE_INVOICES].includes(
    lifecycle.suspendModificationReasonId
  );

  if (lifecycle.isSuspended && !isCancellationPage && !isSupport && !isDev) {
    if (canAccessBilling && isBillingPage) {
      return next();
    }

    if (!isAccessBlockedPage) {
      return next({ name: "PageAccessBlocked" });
    }
  }

  return next();
});
router.onError((error, to) => {
  // this code block resolves a problem with different chunk names that changes after deployment. Read more: https://edrone.atlassian.net/browse/EB-1578
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Unable to preload CSS for") ||
    error.message.includes("error loading dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location = to.fullPath;
    }
  }
});
